<template>
  <div class="operator-storage-dashboard">
    <div class="page-header">
      <h1><i class="bi bi-speedometer2"></i> Dashboard de Almacenamiento</h1>
      <p class="page-description">Gestiona tus tareas de almacenamiento inteligente</p>
    </div>

    <!-- Estadísticas del operario -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon pending">
          <i class="bi bi-clock"></i>
        </div>
        <div class="stat-content">
          <h3>{{ pendingTasks.length }}</h3>
          <p>Tareas Pendientes</p>
        </div>
      </div>
      
      <div class="stat-card">
        <div class="stat-icon in-progress">
          <i class="bi bi-play-circle"></i>
        </div>
        <div class="stat-content">
          <h3>{{ inProgressTasks.length }}</h3>
          <p>En Progreso</p>
        </div>
      </div>
      
      <div class="stat-card">
        <div class="stat-icon completed">
          <i class="bi bi-check-circle"></i>
        </div>
        <div class="stat-content">
          <h3>{{ completedToday.length }}</h3>
          <p>Completadas Hoy</p>
        </div>
      </div>
      
      <div class="stat-card">
        <div class="stat-icon efficiency">
          <i class="bi bi-speedometer"></i>
        </div>
        <div class="stat-content">
          <h3>{{ efficiency }}%</h3>
          <p>Eficiencia</p>
        </div>
      </div>
    </div>

    <!-- Tabs para alternar entre tipos de tareas -->
    <div class="task-tabs">
      <button 
        class="tab-button" 
        :class="{ active: activeTab === 'storage' }"
        @click="activeTab = 'storage'"
      >
        <i class="bi bi-box-arrow-in-down"></i> Almacenamiento
      </button>
      <button 
        class="tab-button" 
        :class="{ active: activeTab === 'picking' }"
        @click="activeTab = 'picking'"
      >
        <i class="bi bi-box-arrow-up"></i> Picking
      </button>
    </div>

    <!-- Contenido de Almacenamiento -->
    <div v-if="activeTab === 'storage'" class="tab-content">
      <div class="tasks-section">
        <h2><i class="bi bi-list-task"></i> Mis Tareas de Almacenamiento</h2>
        
        <div v-if="storageTasks.length === 0" class="empty-state">
          <i class="bi bi-inbox"></i>
          <h3>No tienes tareas de almacenamiento asignadas</h3>
          <p>Las nuevas tareas aparecerán aquí cuando sean asignadas</p>
        </div>

        <div v-else class="tasks-grid">
          <div 
            v-for="task in storageTasks" 
            :key="task.id"
            class="task-card"
            :class="task.status"
            @click="openStorageTask(task)"
          >
            <div class="task-header">
              <div class="task-number">{{ task.list_number }}</div>
              <div class="task-status" :class="task.status">
                {{ getStatusText(task.status) }}
              </div>
            </div>
            
            <div class="task-info">
              <div class="info-item">
                <i class="bi bi-receipt"></i>
                <span>{{ task.transaction?.reference }}</span>
              </div>
              <div class="info-item">
                <i class="bi bi-boxes"></i>
                <span>{{ task.total_items }} items</span>
              </div>
              <div class="info-item">
                <i class="bi bi-clock"></i>
                <span>{{ task.estimated_time }} min</span>
              </div>
              <div class="info-item">
                <i class="bi bi-diagram-3"></i>
                <span>{{ getStrategyText(task.storage_strategy) }}</span>
              </div>
            </div>

            <div class="task-progress" v-if="task.status === 'in_progress'">
              <div class="progress-bar">
                <div 
                  class="progress-fill" 
                  :style="{ width: getTaskProgress(task) + '%' }"
                ></div>
              </div>
              <span class="progress-text">{{ getTaskProgress(task) }}% completado</span>
            </div>

            <div class="task-actions">
              <button 
                v-if="task.status === 'assigned'"
                class="btn btn-primary"
                @click.stop="startStorageTask(task)"
              >
                <i class="bi bi-play"></i> Iniciar
              </button>
              <button 
                v-if="task.status === 'in_progress'"
                class="btn btn-success"
                @click.stop="openStorageTask(task)"
              >
                <i class="bi bi-arrow-right"></i> Continuar
              </button>
              <button 
                v-if="task.status === 'completed'"
                class="btn btn-info"
                @click.stop="viewStorageTask(task)"
              >
                <i class="bi bi-eye"></i> Ver
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Contenido de Picking -->
    <div v-if="activeTab === 'picking'" class="tab-content">
      <div class="tasks-section">
        <h2><i class="bi bi-list-task"></i> Mis Tareas de Picking</h2>
        
        <div v-if="pickingTasks.length === 0" class="empty-state">
          <i class="bi bi-inbox"></i>
          <h3>No tienes tareas de picking asignadas</h3>
          <p>Las nuevas tareas aparecerán aquí cuando sean asignadas</p>
        </div>

        <div v-else class="tasks-grid">
          <div 
            v-for="task in pickingTasks" 
            :key="task.id"
            class="task-card"
            :class="task.status"
            @click="openPickingTask(task)"
          >
            <div class="task-header">
              <div class="task-number">{{ task.list_number }}</div>
              <div class="task-status" :class="task.status">
                {{ getStatusText(task.status) }}
              </div>
            </div>
            
            <div class="task-info">
              <div class="info-item">
                <i class="bi bi-cart"></i>
                <span>{{ task.order?.order_number }}</span>
              </div>
              <div class="info-item">
                <i class="bi bi-boxes"></i>
                <span>{{ task.picking_items?.length || 0 }} items</span>
              </div>
              <div class="info-item">
                <i class="bi bi-clock"></i>
                <span>{{ task.estimated_time }} min</span>
              </div>
              <div class="info-item">
                <i class="bi bi-flag"></i>
                <span>Prioridad {{ task.priority }}</span>
              </div>
            </div>

            <div class="task-progress" v-if="task.status === 'in_progress'">
              <div class="progress-bar">
                <div 
                  class="progress-fill" 
                  :style="{ width: getPickingProgress(task) + '%' }"
                ></div>
              </div>
              <span class="progress-text">{{ getPickingProgress(task) }}% completado</span>
            </div>

            <div class="task-actions">
              <button 
                v-if="task.status === 'assigned'"
                class="btn btn-primary"
                @click.stop="startPickingTask(task)"
              >
                <i class="bi bi-play"></i> Iniciar
              </button>
              <button 
                v-if="task.status === 'in_progress'"
                class="btn btn-success"
                @click.stop="openPickingTask(task)"
              >
                <i class="bi bi-arrow-right"></i> Continuar
              </button>
              <button 
                v-if="task.status === 'completed'"
                class="btn btn-info"
                @click.stop="viewPickingTask(task)"
              >
                <i class="bi bi-eye"></i> Ver
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading overlay -->
    <div v-if="loading" class="loading-overlay">
      <div class="spinner"></div>
      <p>Cargando tareas...</p>
    </div>
  </div>
</template>

<script>
import api from '@/api'

export default {
  name: 'OperatorStorageDashboard',
  data() {
    return {
      loading: false,
      activeTab: 'storage',
      storageTasks: [],
      pickingTasks: [],
      currentUser: null
    }
  },
  computed: {
    allTasks() {
      return [...this.storageTasks, ...this.pickingTasks]
    },
    pendingTasks() {
      return this.allTasks.filter(task => task.status === 'assigned')
    },
    inProgressTasks() {
      return this.allTasks.filter(task => task.status === 'in_progress')
    },
    completedToday() {
      const today = new Date().toDateString()
      return this.allTasks.filter(task => 
        task.status === 'completed' && 
        task.completed_at && 
        new Date(task.completed_at).toDateString() === today
      )
    },
    efficiency() {
      const completed = this.completedToday.length
      const total = this.allTasks.length
      return total > 0 ? Math.round((completed / total) * 100) : 100
    }
  },
  async mounted() {
    this.currentUser = this.$store.getters['auth/currentUser']
    await this.loadTasks()
    
    // Auto-refresh cada 30 segundos
    this.refreshInterval = setInterval(() => {
      this.loadTasks()
    }, 30000)
  },
  beforeUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
  },
  methods: {
    async loadTasks() {
      if (!this.currentUser) return
      
      this.loading = true
      try {
        const [storageResponse, pickingResponse] = await Promise.all([
          api.getStorageLists({ assigned_user_id: this.currentUser.id }),
          api.getPickingLists({ assigned_user_id: this.currentUser.id })
        ])
        
        this.storageTasks = storageResponse.data
        this.pickingTasks = pickingResponse.data
      } catch (error) {
        console.error('Error loading tasks:', error)
      } finally {
        this.loading = false
      }
    },

    async startStorageTask(task) {
      try {
        await api.startStorageList(task.id)
        await this.loadTasks()
        this.$router.push(`/storage-execution/${task.id}`)
      } catch (error) {
        console.error('Error starting storage task:', error)
      }
    },

    openStorageTask(task) {
      this.$router.push(`/storage-execution/${task.id}`)
    },

    viewStorageTask(task) {
      this.$router.push(`/storage-execution/${task.id}?view=true`)
    },

    async startPickingTask(task) {
      try {
        await api.startPickingList(task.id)
        await this.loadTasks()
        this.$router.push(`/intelligent-picking/${task.id}`)
      } catch (error) {
        console.error('Error starting picking task:', error)
      }
    },

    openPickingTask(task) {
      this.$router.push(`/intelligent-picking/${task.id}`)
    },

    viewPickingTask(task) {
      this.$router.push(`/intelligent-picking/${task.id}?view=true`)
    },

    getTaskProgress(task) {
      if (!task.storage_items || task.storage_items.length === 0) return 0
      const completed = task.storage_items.filter(item => item.status === 'stored').length
      return Math.round((completed / task.storage_items.length) * 100)
    },

    getPickingProgress(task) {
      if (!task.picking_items || task.picking_items.length === 0) return 0
      const completed = task.picking_items.filter(item => item.status === 'picked').length
      return Math.round((completed / task.picking_items.length) * 100)
    },

    getStatusText(status) {
      const texts = {
        'assigned': 'Asignado',
        'in_progress': 'En Progreso',
        'completed': 'Completado',
        'pending': 'Pendiente'
      }
      return texts[status] || status
    },

    getStrategyText(strategy) {
      const strategies = {
        'abc_optimized': 'ABC Optimizado',
        'chaotic_controlled': 'Caótico Controlado',
        'zone_dedicated': 'Zonas Dedicadas',
        'fifo_optimized': 'FIFO Optimizado',
        'space_optimized': 'Espacio Optimizado'
      }
      return strategies[strategy] || strategy
    }
  }
}
</script>

<style scoped>
.operator-storage-dashboard {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  min-height: 100vh;
  background-color: #f8f9fa;
}

.page-header {
  margin-bottom: 30px;
  text-align: center;
}

.page-header h1 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 2.5rem;
  font-weight: 600;
}

.page-header h1 i {
  margin-right: 15px;
  color: #3498db;
}

.page-description {
  color: #7f8c8d;
  font-size: 1.2rem;
  margin: 0;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  transition: transform 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  font-size: 1.5rem;
  color: white;
}

.stat-icon.pending { background: #f39c12; }
.stat-icon.in-progress { background: #3498db; }
.stat-icon.completed { background: #27ae60; }
.stat-icon.efficiency { background: #9b59b6; }

.stat-content h3 {
  font-size: 2rem;
  font-weight: bold;
  margin: 0;
  color: #2c3e50;
}

.stat-content p {
  margin: 5px 0 0 0;
  color: #7f8c8d;
  font-size: 0.9rem;
}

.task-tabs {
  display: flex;
  margin-bottom: 30px;
  border-bottom: 2px solid #ecf0f1;
  background: white;
  border-radius: 8px 8px 0 0;
  overflow: hidden;
}

.tab-button {
  background: none;
  border: none;
  padding: 20px 30px;
  font-size: 1.1rem;
  font-weight: 500;
  color: #7f8c8d;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  justify-content: center;
}

.tab-button:hover {
  color: #3498db;
  background-color: #f8f9fa;
}

.tab-button.active {
  color: #3498db;
  background-color: #e3f2fd;
  border-bottom: 3px solid #3498db;
}

.tab-button i {
  font-size: 1.2rem;
}

.tab-content {
  animation: fadeIn 0.3s ease-in-out;
}

.tasks-section h2 {
  color: #2c3e50;
  margin-bottom: 25px;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: 10px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.empty-state i {
  font-size: 4rem;
  color: #bdc3c7;
  margin-bottom: 20px;
}

.empty-state h3 {
  color: #7f8c8d;
  margin-bottom: 10px;
}

.empty-state p {
  color: #95a5a6;
}

.tasks-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
}

.task-card {
  background: white;
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  cursor: pointer;
  transition: all 0.3s ease;
  border-left: 4px solid #ecf0f1;
}

.task-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
}

.task-card.assigned {
  border-left-color: #f39c12;
}

.task-card.in_progress {
  border-left-color: #3498db;
}

.task-card.completed {
  border-left-color: #27ae60;
}

.task-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.task-number {
  font-size: 1.2rem;
  font-weight: bold;
  color: #2c3e50;
}

.task-status {
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: uppercase;
}

.task-status.assigned {
  background: #fef9e7;
  color: #f39c12;
}

.task-status.in_progress {
  background: #e3f2fd;
  color: #3498db;
}

.task-status.completed {
  background: #e8f5e8;
  color: #27ae60;
}

.task-info {
  margin-bottom: 20px;
}

.info-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  color: #7f8c8d;
  font-size: 0.9rem;
}

.info-item i {
  margin-right: 10px;
  width: 16px;
  color: #3498db;
}

.task-progress {
  margin-bottom: 20px;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #ecf0f1;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 5px;
}

.progress-fill {
  height: 100%;
  background: #3498db;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 0.8rem;
  color: #7f8c8d;
}

.task-actions {
  display: flex;
  gap: 10px;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 5px;
}

.btn-primary {
  background: #3498db;
  color: white;
}

.btn-primary:hover {
  background: #2980b9;
}

.btn-success {
  background: #27ae60;
  color: white;
}

.btn-success:hover {
  background: #229954;
}

.btn-info {
  background: #17a2b8;
  color: white;
}

.btn-info:hover {
  background: #138496;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #ecf0f1;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .operator-storage-dashboard {
    padding: 15px;
  }
  
  .page-header h1 {
    font-size: 2rem;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .task-tabs {
    flex-direction: column;
  }
  
  .tab-button {
    text-align: left;
    justify-content: flex-start;
  }
  
  .tasks-grid {
    grid-template-columns: 1fr;
  }
}
</style> 